import React, { useState } from 'react';
import { Tag, Empty, Spin, Collapse, Drawer, Tooltip } from 'antd';
import { QuestionCircleOutlined, SearchOutlined, CloseCircleFilled, LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { financialApi } from '../../utils/financialApi';
import { extractApiUserMessage } from '../../utils/api';
import { formatPriceToman } from '../../utils/formatPriceToman';
import { toJalali } from '../../utils/dateUtils';

const HINT = 'شماره موبایل، نام مشتری، شماره پیگیری، شماره سفارش، مبلغ، شماره فاکتور، شماره چک یا شماره صیاد را وارد کنید.';

const GROUPS = [
  { key: 'clients', label: 'مشتری‌ها', link: (i) => `/finance/client-profile/${i.id}` },
  { key: 'deals', label: 'معاملات (فروش)', link: (i) => `/deals/${i.id}?tab=billing` },
  { key: 'payments', label: 'پرداخت‌ها', link: (i) => `/deals/${i.deal_id}?tab=billing` },
  { key: 'transactions', label: 'تراکنش‌ها', link: () => '/finance/transactions' },
  { key: 'invoices', label: 'فاکتورها', link: (i) => `/finance/invoices/${i.id}` },
  { key: 'orders', label: 'سفارش‌ها', link: (i) => `/orders/${i.id}` },
  { key: 'cheques', label: 'چک‌ها', link: () => '/finance/cheques' },
];

const STATUS_LABELS = {
  pending_approval: 'در انتظار تأیید', approved: 'تأیید شده', rejected: 'رد شده', needs_correction: 'نیاز به اصلاح',
  in_vault: 'نزد صندوق', deposited: 'واگذار به بانک', cleared: 'پاس شده', bounced: 'برگشتی', returned: 'عودت', spent: 'خرج شده',
  settled: 'تسویه‌شده', partially_paid: 'پرداخت ناقص', unpaid: 'پرداخت‌نشده', completed: 'تکمیل‌شده', paid: 'پرداخت‌شده',
};

/**
 * باکس جستجوی یکپارچه مالی؛ در همه صفحات بخش مالی نمایش داده می‌شود
 * و نتیجه در پنل کنار همان صفحه باز می‌شود (بدون تغییر صفحه).
 */
const FinancialSearchBox = ({ isDarkMode = false }) => {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const runSearch = async (raw) => {
    const q = (raw || '').trim();
    if (q.length < 2) {
      setError('حداقل ۲ کاراکتر وارد کنید.');
      setResult(null);
      setOpen(true);
      return;
    }
    setError('');
    setOpen(true);
    setLoading(true);
    try {
      const res = await financialApi.globalSearch(q);
      setResult(res?.data || res);
    } catch (err) {
      setResult(null);
      setError(extractApiUserMessage(err) || 'خطا در جستجو');
    } finally {
      setLoading(false);
    }
  };

  const groups = result?.groups || {};
  const nonEmpty = GROUPS.filter((g) => (groups[g.key]?.total || 0) > 0);
  const grandTotal = GROUPS.reduce((sum, g) => sum + (groups[g.key]?.total || 0), 0);

  const goTo = (to) => {
    setOpen(false);
    navigate(to);
  };

  const renderItem = (group, item) => {
    const to = group.link ? group.link(item) : null;
    return (
      <div
        key={`${group.key}-${item.id}`}
        onClick={to ? () => goTo(to) : undefined}
        className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 rounded-lg border border-gray-100 dark:border-gray-700 mb-2 ${to ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800' : ''}`}
      >
        <div className="min-w-0">
          <div className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{item.title}</div>
          {item.subtitle ? <div className="text-xs text-gray-500 mt-0.5">{item.subtitle}</div> : null}
        </div>
        <div className="flex items-center gap-3 text-xs">
          {item.amount != null && item.amount > 0 && (
            <span className="font-bold text-emerald-600">{formatPriceToman(item.amount)}</span>
          )}
          {item.date && <span className="text-gray-500">{toJalali(item.date)}</span>}
          {item.status && <Tag style={{ margin: 0 }}>{STATUS_LABELS[item.status] || item.status}</Tag>}
        </div>
      </div>
    );
  };

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          runSearch(value);
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={`flex items-center gap-2 w-full rounded-xl border px-3 py-1.5 shadow-sm transition-all
          ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}
        style={{
          fontFamily: 'CustomFont',
          ...(focused ? { borderColor: '#0d887e', boxShadow: '0 0 0 3px rgba(13,136,126,0.18)' } : {}),
        }}
      >
        <SearchOutlined className="text-lg text-brand-primary" />
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="جستجو در مشتری، فروش، تراکنش، فاکتور، پرداخت و چک…"
          className={`flex-1 min-w-0 bg-transparent outline-none text-sm py-1.5 ${isDarkMode ? 'text-white placeholder-gray-400' : 'text-gray-800 placeholder-gray-400'}`}
          style={{ fontFamily: 'CustomFont' }}
        />
        {value && (
          <button
            type="button"
            onClick={() => { setValue(''); setResult(null); setError(''); }}
            className="text-gray-400 hover:text-gray-600 flex items-center"
            aria-label="پاک کردن"
          >
            <CloseCircleFilled />
          </button>
        )}
        <Tooltip title={<span style={{ fontFamily: 'CustomFont' }}>{HINT}</span>} placement="bottom">
          <QuestionCircleOutlined className="text-base text-gray-500 cursor-help" />
        </Tooltip>
        <button
          type="submit"
          disabled={loading}
          className="flex items-center gap-1.5 rounded-lg bg-brand-primary hover:bg-brand-primary-hover text-white text-sm font-semibold px-4 py-1.5 transition-colors disabled:opacity-60"
          style={{ fontFamily: 'CustomFont' }}
        >
          {loading ? <LoadingOutlined /> : <SearchOutlined />}
          جستجو
        </button>
      </form>

      <Drawer
        title={<span style={{ fontFamily: 'CustomFont' }}>نتایج جستجو{result?.query ? `: ${result.query}` : ''}</span>}
        open={open}
        onClose={() => setOpen(false)}
        width={520}
        placement="left"
        styles={{ body: { fontFamily: 'CustomFont' } }}
      >
        <Spin spinning={loading}>
          {error && <div className="text-red-500 text-sm mb-3">{error}</div>}
          {result && !error && grandTotal === 0 && !loading && <Empty description="نتیجه‌ای یافت نشد" />}
          {result && !error && grandTotal > 0 && (
            <>
              <div className="mb-3 flex flex-wrap gap-2">
                {nonEmpty.map((g) => (
                  <Tag key={g.key} color="blue">{g.label}: {groups[g.key].total}</Tag>
                ))}
              </div>
              <Collapse
                defaultActiveKey={nonEmpty.map((g) => g.key)}
                items={nonEmpty.map((g) => ({
                  key: g.key,
                  label: <span style={{ fontFamily: 'CustomFont' }}>{g.label} ({groups[g.key].total})</span>,
                  children: (
                    <div>
                      {groups[g.key].items.map((it) => renderItem(g, it))}
                      {groups[g.key].total > groups[g.key].items.length && (
                        <div className="text-xs text-gray-400 mt-1">
                          فقط {groups[g.key].items.length} مورد اول نمایش داده شد؛ عبارت جستجو را دقیق‌تر کنید.
                        </div>
                      )}
                    </div>
                  ),
                }))}
              />
            </>
          )}
        </Spin>
      </Drawer>
    </>
  );
};

export default FinancialSearchBox;
